import { plural } from './format';
import type { Messages } from './en';
import { overviewEs } from './areas/overview';
import { setupEs } from './areas/setup';
import { siteEs } from './areas/site';
import { catalogEs } from './areas/catalog';
import { connectionsEs } from './areas/connections';
import { activityEs } from './areas/activity';
import { featuresEs } from './areas/features';
import { builderEs } from './areas/builder';
import { loginsEs } from './areas/logins';

/**
 * Spanish, for the readers docs/i18n-design.md §1 actually names: *"roughly a
 * sixth of households speak Spanish (Puerto Rican and Dominican communities)"*.
 * An operator seat is staffed from the same island as everything else.
 *
 * ## The register, and why it is not Peninsular
 *
 * **Neutral Latin American Spanish, `usted` throughout.** Never `vosotros`,
 * which no one in the Caribbean uses; not `vale`, `coger`, `móvil` or
 * `ordenador`. A Puerto Rican or Dominican operator should read this and not
 * notice it was translated. Where the same concept appears in
 * `apps/site/src/messages/es.ts` the wording matches it — *la agenda* for the
 * book, *huésped* for a guest, *reservar* for booking — because two catalogs
 * that disagreed about the same noun would teach two vocabularies for one
 * system.
 *
 * ## This surface is technical, and two registers live inside it
 *
 * Most of this file is a **console**: precise, unfussy, short. Column headings
 * are nouns and buttons are verbs, and nothing is softened.
 *
 * The exception is deliberate and it is the reason `status.consequence.*` and
 * the confirm dialogs read the way they do. Those sentences are quoted to
 * somebody a second before they change what an AI agent promises a stranger on
 * the phone, and English keeps them **grave** — *"From this moment we hold the
 * book"*, *"every bookmark 404s"*, *"the id is never reused"*. Spanish keeps
 * them exactly as grave. A dialog that reads lighter in one language than the
 * other is a dialog that is easier to click through in that language.
 *
 * ## What is not translated, and it is most of the nouns
 *
 * Module slugs, scope names, `tenant.status`, `authority`, `plan`, job states,
 * delivery statuses, every identifier inside `` ` `` — and, above all, **the
 * server's own sentences**, which never reach this file at all (`en.ts` says
 * why). A refusal from `admin-api` prints in English on a Spanish screen
 * because it is the sentence the CLI prints and the sentence the log carries,
 * and paraphrasing it here would make the console the vaguest description of
 * what happened.
 *
 * ## What is asserted about this file
 *
 * The declaration below is `: Messages`: a key this file forgets is a **build
 * failure**, and a template whose parameters drift from the English one is a
 * build failure. There is no runtime fallback to English anywhere, on purpose.
 */

export const es: Messages = {
  /* The redesign's areas, one file each (./areas/*). */
  ...overviewEs,
  ...setupEs,
  ...siteEs,
  ...catalogEs,
  ...connectionsEs,
  ...activityEs,
  ...featuresEs,
  ...builderEs,
  ...loginsEs,
  /* ---------------------------------------------------------------- */
  /* Palabras que usa más de una pantalla                              */
  /* ---------------------------------------------------------------- */

  'common.cancel': 'Cancelar',
  'common.continue': 'Continuar',
  'common.back': 'Atrás',
  'common.backToFleet': '← Negocios',
  'common.refresh': 'Actualizar',
  'common.remove': 'quitar',
  'common.hide': 'Ocultar',
  'common.never': 'nunca',
  'common.working': 'Procesando…',
  'common.saving': 'Guardando…',
  'common.starting': 'Iniciando…',
  'common.issuing': 'Emitiendo…',
  'common.applying': 'Aplicando…',
  'common.creating': 'Creando…',
  'common.name': 'Nombre',
  'common.status': 'Estado',
  'common.integration': 'Integración',
  // "Tenant" is `inquilino` — the word Spanish cloud documentation already uses
  // for multiinquilino — rather than a business word like `negocio`, because the
  // row it names is a tenant in the technical sense and its id is a slug.
  'common.tenant': 'Negocio',
  'common.plan': 'Plan',
  'common.url': 'URL',
  'common.when': 'Cuándo',
  // `audit_log.actor` is a person, and a log column reads better as the question
  // it answers than as the Latin noun both languages happen to share.
  'common.actor': 'Quién',
  'common.action': 'Acción',
  'common.target': 'Objetivo',

  'common.reading': ({ what }) => `Leyendo ${what}…`,
  'loading.fleet': 'la flota',
  'loading.tenant': 'el negocio',
  'loading.domains': 'las direcciones web del negocio',
  'loading.theme': 'el diseño del sitio del negocio',
  'loading.deliveries': 'el registro de entregas',
  'loading.executions': 'las ejecuciones',
  'loading.execution': 'la ejecución',

  /* ---------------------------------------------------------------- */
  /* El armazón                                                        */
  /* ---------------------------------------------------------------- */

  'app.seat': 'operador',
  'app.nav.label': 'Consola',
  'app.tab.fleet': 'Negocios',
  'app.tab.audit': 'Registro',
  'app.tab.jobs': 'Comprobaciones e importaciones',
  'app.devToken': 'token de desarrollo',
  'app.devToken.title': 'Token de desarrollo sin firmar',
  'app.signOut': 'Cerrar sesión',
  'app.footer':
    'Todo lo que se hace en esta consola — también mirar — queda anotado en el registro.',

  /* ---------------------------------------------------------------- */
  /* Inicio de sesión                                                  */
  /* ---------------------------------------------------------------- */

  'signin.title': 'Operador de clearDay',
  'signin.sub':
    'Para los operadores de JSWaves. Entre con una cuenta de operador; el acceso del personal de un negocio no abre la consola.',
  'signin.dev':
    'Inicio de sesión de desarrollo. Cualquier correo funciona y el token no está firmado — esta compilación solo habla con un `admin-api` iniciado con `AUTH_FAKE_TOKENS=1`, que se niega a ejecutarse en producción.',
  'signin.email': 'Correo electrónico',
  'signin.password': 'Contraseña',
  'signin.submit': 'Iniciar sesión',
  'signin.busy': 'Iniciando sesión…',
  'signin.unavailable':
    'No se pudo comunicar con el servicio de inicio de sesión. Revise la conexión.',
  'signin.rejected': 'Ese correo y esa contraseña no coinciden.',

  /* ---------------------------------------------------------------- */
  /* Las piezas pequeñas                                               */
  /* ---------------------------------------------------------------- */

  'failure.byDesign.title': 'No en esta superficie — por diseño',
  'failure.byDesign.rule':
    'La consola de operador nunca recorre los registros propios de un negocio — sus reservas, sus clientes y su dinero. Esto es ese candado funcionando, no una falla que reportar.',

  /**
   * Las tres frases que ningún servidor escribió — véase `en.ts`. Todo lo demás
   * que imprime un `Failure` viene del servicio y se muestra tal cual; estas
   * son de la consola, así que se traducen como cualquier otra palabra suya.
   */
  'failure.offline': 'No se pudo contactar con admin-api.',
  /**
   * El número es el dato, y `HTTP` se escribe igual en los dos idiomas: la
   * entrada es una función porque toma un parámetro, no porque haya algo que
   * traducir en ella.
   */
  'failure.http': ({ status }: { status: number }) => `HTTP ${String(status)}.`,
  'failure.malformed': 'admin-api envió una forma que esta consola no conoce.',

  'once.note':
    'Esta es la única vez que se muestra la clave. Solo se guarda su hash SHA-256, así que no se puede recuperar — si se pierde, revóquela y emita otra.',
  'once.copy': 'Copiar',
  'once.copied': 'Copiado',
  'once.stored': 'Ya la guardé',

  /* ---------------------------------------------------------------- */
  /* La flota                                                          */
  /* ---------------------------------------------------------------- */

  'fleet.filter.any': 'cualquiera',
  'fleet.revoked': ({ count }) => ` (+${String(count)} ${plural(count, 'revocada', 'revocadas')})`,

  'pager.previous': 'Anterior',
  'pager.next': 'Siguiente',
  'pager.range': ({ from, to }) => `${from}–${to}`,
  'pager.rangeOf': ({ from, to, total }) => `${from}–${to} de ${total}`,

  /* ---------------------------------------------------------------- */
  /* La página del inquilino                                           */
  /* ---------------------------------------------------------------- */

  'tenant.audit': 'Registro de este negocio',
  'tenant.jobs': 'Comprobaciones e importaciones',

  /* "Ver lo que ella ve" — la vista de soporte de solo lectura (status §5; X5). */
  'support.note': ({ name }) =>
    `Abre la app del personal de ${name} en solo lectura durante 15 minutos. Cada vista queda anotada en el registro; nada puede cambiarse a través de ella.`,
  'support.button': 'Ver lo que ven',
  'support.opening': 'Abriendo…',
  'support.opened': ({ until, auditId }) =>
    `Abierta en una pestaña nueva — solo lectura hasta ${until}, registrada como ${auditId}.`,
  'status.meaning.onboarding': 'En configuración. El socio todavía no está conectado.',
  'status.meaning.importing':
    'Funcionando junto a su sistema anterior mientras se traslada su libro — a los socios se les dice que la disponibilidad es aproximada.',
  'status.meaning.live':
    'Nosotros llevamos su libro. Es la única etapa en la que a los socios se les dice que la disponibilidad es exacta.',
  'status.meaning.paused':
    'Sin responder por ahora: los socios no pueden ver la disponibilidad ni reservar.',
  'status.meaning.offboarded':
    'Terminó con nosotros. No se puede reservar nada y no se comparten datos de clientes.',

  /* ---------------------------------------------------------------- */
  /* El simulador de ofertas (docs/lovespa-gaps-plan.md A2–A4)         */
  /* ---------------------------------------------------------------- */

  'sim.title': 'Simulador de ofertas',
  'sim.note':
    'Escriba un día del libro del negocio y lea qué ofrecería cada política para él. Nada de esto se lee ni se escribe en su libro — el motor corre en este navegador sobre lo que usted escriba.',
  'sim.paperNote':
    'La antelación mínima y el corte del mismo día están en el formulario de abajo y **no** en las columnas a propósito: deciden *cuándo* respondemos, las columnas deciden *qué* horas nombramos, y una columna vacía nunca debería significar «puso dos semanas de antelación».',
  'sim.date': 'El día',
  'sim.openFrom': 'Abre a las',
  'sim.openTo': 'Cierra a las',
  'sim.grid': 'Inicios cada (minutos)',
  'sim.gap': 'Espacio entre clientes (minutos)',
  'sim.gapHint':
    'El valor de la casa. Un artículo con su propio margen lo conserva; uno en cero toma este.',
  'sim.ask': 'Lo que piden (minutos)',
  'sim.shortest': 'Tratamiento más corto (minutos)',
  'sim.shortestHint':
    'Sirve para calcular qué hueco vale la pena — lo más corto de la carta, más el espacio.',
  'sim.bookings': 'Lo que ya está en el libro',
  'sim.bookingsHint': ({ gap }: { gap: number }) =>
    `Escriba la duración del tratamiento, como en el libro de papel. Los ${String(gap)} minutos de espacio se suman aparte.`,
  'sim.bookingStart': 'Empieza',
  'sim.bookingLength': 'Minutos',
  'sim.bookingStartLabel': ({ row }: { row: number }) => `Inicio de la cita ${String(row)}`,
  'sim.bookingLengthLabel': ({ row }: { row: number }) => `Duración de la cita ${String(row)}`,
  'sim.addBooking': 'Agregar una cita',
  'sim.keptCount': ({ kept }: { kept: number }) =>
    `${String(kept)} hora${kept === 1 ? '' : 's'} ofrecida${kept === 1 ? '' : 's'}`,
  'sim.relaxed': ({ applied }: { applied: string }) =>
    `Esta columna no ofreció nada, así que la respuesta vino de la regla que ${applied}.`,
  'sim.nothing': 'Ningún inicio libre en este día.',
  'sim.lead': 'primero',
  'sim.leadsWith': ({ times }: { times: string }) =>
    times === '' ? 'No hay nada con qué empezar.' : `Un asistente empezaría por ${times}.`,
  'sim.choose': 'Usar este',
  'sim.chosen': 'Elegido',
  'sim.reason.pool': 'Un recurso agrupado — la política no se le aplica.',
  'sim.reason.sliverBefore': ({ minutes }: { minutes: number }) =>
    `deja ${String(minutes)} minutos libres antes`,
  'sim.reason.sliverAfter': ({ minutes }: { minutes: number }) =>
    `deja ${String(minutes)} minutos libres después`,
  'sim.reason.floating': 'queda en el medio — no toca nada',
  'sim.reason.notAnchor': 'no es una de las horas fijas del día',
  'sim.reason.paced': 'el tope de la cocina ya está lleno en ese paso',
  'sim.reason.taken': 'la sala ya está prometida a esa hora',
  'sim.reason.overCap': 'pasa del número de horas que lleva una respuesta',
  'sim.policyTitle': 'La regla de reservas de este negocio',
  'sim.policyNote':
    'Estos cinco ajustes deciden qué horas se ofrecen a clientes y socios. Nada de esto toca su libro, sus horarios ni sus precios, y a quien atiende reservando a mano no se le recorta nunca.',
  'sim.rank': 'Orden',
  'sim.rankHint':
    'Solo orden — «packing» lee primero las que cierran un hueco. Nada deja de reservarse por quedar al final.',
  'sim.usefulGap': 'Hueco aprovechable (minutos)',
  'sim.usefulGapHint': 'Déjelo vacío para calcularlo desde la carta.',
  'sim.anchors': 'Horas fijas en un día vacío',
  'sim.anchorsHint':
    'Horas locales, separadas por comas. Vacío significa la apertura y el último inicio que quepa.',
  'sim.topPartner': 'Empezar por (agente)',
  'sim.topGuest': 'Empezar por (sitio del cliente)',
  'sim.topHint': 'Cuántas se dicen primero. Las demás siguen reservables.',
  'sim.bufferBefore': 'Espacio antes (minutos)',
  'sim.bufferAfter': 'Espacio después (minutos)',
  'sim.bufferAfterHint': '«Dejo media hora entre clientes».',
  'sim.minLead': 'Antelación mínima (minutos)',
  'sim.leadHint':
    'Con cuánta anticipación puede reservar un desconocido. Cero no pone más piso que «no en el pasado».',
  'sim.cutoff': 'Corte del mismo día',
  'sim.cutoffHint': 'Después de esta hora local, hoy deja de ofrecerse.',
  'sim.protectedTitle': 'Horas apartadas',
  'sim.protectedNote':
    'Nunca se ofrecen a un cliente ni a un agente; el dueño o la dueña sí puede poner a alguien ahí. La hora del almuerzo, por ejemplo.',
  'sim.protectedDayLabel': ({ row, day }: { row: number; day: string }) =>
    `${day} en las horas reservadas ${String(row)}`,
  'sim.protectedFromLabel': ({ row }: { row: number }) => `Horas reservadas ${String(row)}, desde`,
  'sim.protectedToLabel': ({ row }: { row: number }) => `Horas reservadas ${String(row)}, hasta`,
  'sim.addProtected': 'Reservar unas horas',
  'sim.copy': 'Copiar los ajustes',
  'sim.copied': 'Copiado',
  'sim.apply': 'Guardar como su regla de reservas',
  'sim.applied': ({ mode, gap, lead }: { mode: string; gap: number; lead: number }) =>
    `Guardado. La regla de reservas ${mode}, con ${String(gap)} minutos de espacio y ${String(lead)} minutos de antelación.`,

  /* ---------------------------------------------------------------- */
  /* Las seis respuestas del mostrador (B4, C1, §2.3, spec §3.6)       */
  /* ---------------------------------------------------------------- */

  /**
   * Seis preguntas en las palabras que usaría una dueña. Los tokens de abajo —
   * `at_pickup`, `asap`, `request` — se quedan en la CLI y en la bitácora, donde
   * quien lee es alguien mirando un registro; aquí son frases, porque quien lee
   * está sentado al lado de la dueña.
   */
  'settings.saved': ({ tenders, payment, booking }) =>
    `Guardado. La caja muestra ${tenders}; las recogidas se pagan ${payment}; el sitio web: ${booking}`,

  'settings.tenderLabel': ({ tender }) => `Mostrar el botón de ${tender}`,
  'settings.noTenders': 'Deje al menos un botón encendido, o no habrá nada que pulsar en la caja.',

  'settings.pickup': '¿Los huéspedes pueden recoger en persona?',
  'settings.pickupHint':
    'Reservar en línea y recoger en el mostrador. Encenderlo es el momento en que un desconocido puede apartar algo del estante antes de que nadie haya cobrado.',
  'settings.pickup.yes': 'Sí — pueden reservar y recoger',
  'settings.pickup.no': 'No — no se recoge nada en persona',
  'settings.ship': '¿Enviamos cosas por correo?',
  'settings.shipHint':
    'La tienda por envío: cotización de envío en vivo y enlace de pago. Es lo que hace toda tienda de la flota salvo que alguien diga lo contrario.',
  'settings.ship.yes': 'Sí — enviamos los pedidos',
  'settings.ship.no': 'No — no se envía nada',

  'settings.payment': '¿Cuándo pagan?',
  'settings.paymentHint':
    'Pagar al reservar deja registrado lo que se debe, pero todavía no envía a nadie a pagarlo — alguien tiene que pedirlo igual. Sin configurar, esto sigue a si el negocio puede cobrar en línea o no.',
  'settings.payment.atPickup': 'En el mostrador, cuando se lo llevan',
  'settings.payment.prepay': 'Al reservarlo',

  'settings.expiry': 'Cuántos días guardamos una recogida',
  'settings.expiryHint':
    'Se cuenta desde el día en que se marca como lista. Se lee una sola vez, cuando algo queda listo, así que cambiarlo no mueve nada que ya esté esperando en el estante.',

  'settings.timing': 'Para cuándo son los pedidos para llevar',
  'settings.timingHint':
    'Un mostrador que fríe al momento no puede prometer una hora; un servicio de banquetes no puede trabajar sin una. El servidor hace cumplir lo que se elija, así que a un huésped nunca se le pregunta algo que el negocio ya respondió.',
  'settings.timing.asap': 'En cuanto esté listo — sin horas en ninguna parte',
  'settings.timing.scheduled': 'Para la hora que elijan — siempre',
  'settings.timing.either': 'Cualquiera: cuanto antes, o programado',

  /* Los controles de la cocina (docs/verticals-plan.md V1 / R4). */
  'settings.confirmation': '¿Cómo confirma la cocina un pedido?',
  'settings.confirmationHint':
    'Una persona confirma cada pedido desde el pase y dice cuánto tarda, o cada pedido se confirma en cuanto llega con el tiempo de preparación predeterminado. No se envía ningún mensaje al cliente hasta que un pedido se confirma con una hora.',
  'settings.confirmation.staff': 'Una persona confirma cada pedido',
  'settings.confirmation.auto': 'Confirmado en cuanto se hace',
  'settings.prep': 'Tiempo de preparación predeterminado (minutos)',
  'settings.prepHint':
    'Con lo que se rellena el botón Confirmar y lo que escribe una confirmación automática. Déjalo vacío para no tener predeterminado: entonces un pedido confirmado sin hora no envía ningún mensaje. Cero significa «listo ya» y sí se envía.',
  'settings.prepNone': 'ninguno',

  'settings.booking': '¿El sitio web reserva o pregunta?',
  'settings.bookingHint':
    'Solo su propio sitio. Ni un agente al teléfono ni una persona del equipo en el mostrador quedan limitados por esto, y un cliente siempre puede ver la disponibilidad, ver sus propias visitas y cancelar una.',
  'settings.booking.open': 'Cualquiera puede reservar por su cuenta',
  'settings.booking.trusted': 'Los clientes de confianza reservan; el resto pregunta',
  'settings.booking.request': 'Todos preguntan, y el negocio confirma',

  'settings.hold': '¿Cuánto tiempo guarda una solicitud su hora?',
  'settings.holdHint':
    'Horas, de 1 a 168. Una solicitud sin responder conserva su hora durante ese tiempo y luego caduca, y se le avisa al cliente. Cambiar esto no mueve una solicitud que ya está esperando — su plazo se fija cuando el cliente la envía.',

  /* ---------------------------------------------------------------- */
  /* Claves                                                            */
  /* ---------------------------------------------------------------- */

  'keys.issue': 'Emitir la clave',

  /* ---------------------------------------------------------------- */
  /* Destinos de webhook                                               */
  /* ---------------------------------------------------------------- */

  'webhooks.secretRef': 'Referencia al secreto',

  /* ---------------------------------------------------------------- */
  /* Dominios (spec §6.4.2)                                            */
  /* ---------------------------------------------------------------- */

  'domains.yes': 'sí',
  'domains.no': 'no',

  /* ---------------------------------------------------------------- */
  /* Trabajos                                                          */
  /* ---------------------------------------------------------------- */

  'jobs.title': 'Comprobaciones e importaciones',
  'jobs.navLabel': 'Trabajo',
  'jobs.tab.conformance': 'Conformidad',
  'jobs.tab.import': 'Importación',
  'jobs.unconfigured.title': 'Este trabajo no está configurado en este entorno',
  'jobs.unconfigured.rule':
    'La variable es opcional a propósito: una consola desplegada antes de que existan sus Cloud Run Jobs sigue siendo la consola con la que terminaría de crearlos. Nada está roto — todavía no hay nada que disparar.',

  'jobs.conformance.title': 'Ejecutar la suite de conformidad',
  'jobs.conformance.note':
    'Comprueba cada promesa del acuerdo con los socios contra el libro real de un negocio. Espera los negocios de prueba preparados — una corrida contra un negocio que nadie preparó falla y no significa nada.',
  'jobs.baseUrl': 'URL base alternativa',
  'jobs.baseUrlPlaceholder': 'déjelo vacío para usar el PROVIDER_BASE_URL configurado del trabajo',
  'jobs.run': 'Ejecutar la comprobación del contrato con socios',
  'jobs.started': ({ tenantId, detail }) => `Iniciado contra ${tenantId}. ${detail}`,
  'jobs.execution': ({ executionId }) => `Ejecución ${executionId}.`,

  'jobs.import.title': 'Importar una base de datos de LoveSpa',
  'jobs.import.note':
    'Primero una simulación, siempre. Una simulación lee el archivo de la app anterior y reporta lo que escribiría; solo una ejecución real escribe algo, y que los totales cuadren al centavo es lo que permite el traspaso.',
  'jobs.import.startedDry': ({ tenantId, detail }) =>
    `Simulación iniciada contra ${tenantId}. ${detail}`,
  'jobs.import.startedExecute': ({ tenantId, detail }) =>
    `IMPORTACIÓN REAL iniciada contra ${tenantId}. ${detail}`,
  'jobs.import.file': 'Ruta del archivo',
  'jobs.import.fileHint':
    'Donde sea que el trabajo pueda alcanzarlo. Nada se sube a través de esta consola.',
  'jobs.import.mode': 'Modo',
  'jobs.import.dryRun':
    '**Simulación** — lee el archivo y reporta. No escribe nada. Es lo predeterminado y lo es a propósito.',
  'jobs.import.execute':
    '**Ejecutar** — escribe el historial en este negocio. Haga primero una simulación y lea sus totales; esto no se puede deshacer desde la consola.',
  'jobs.import.startDry': 'Iniciar la simulación',
  'jobs.import.startExecute': 'Ejecutar la importación',
  'jobs.import.confirmTitle': ({ tenantId }) => `¿Ejecutar la importación en ${tenantId}?`,
  'jobs.import.confirmConsequence':
    'Esto escribe un año de historial en el negocio de verdad. El traspaso espera una simulación cuyos totales cuadren al centavo — si no ha leído una para este archivo, cancele y haga una simulación.',
  'jobs.import.confirmLabel': 'Ejecutar la importación',

  'jobs.executions.title': 'Ejecuciones recientes',
  'jobs.executions.note':
    'El estado se deriva de los contadores de tareas, no se lee de un campo de estado — una ejecución con nombre y sin hora de finalización está corriendo; una que terminó con una tarea fallida está fallida.',
  'jobs.executions.empty': 'Todavía no se ha ejecutado nada.',
  'jobs.th.execution': 'Ejecución',
  'jobs.th.state': 'Estado',
  'jobs.th.started': 'Iniciada',
  'jobs.th.completed': 'Completada',
  'jobs.th.tasks': 'Tareas',
  'jobs.running': 'en ejecución',
  'jobs.tasks': ({ succeeded, total }) => `${String(succeeded)}/${String(total)}`,
  'jobs.failedFlag': ({ count }) => `${String(count)} ${plural(count, 'fallida', 'fallidas')}`,
  'jobs.detail': 'Detalle',
  'jobs.dt.resource': 'Recurso',
  'jobs.dt.created': 'Creada',
  'jobs.logsElsewhere':
    'La salida no está aquí. Los registros de un trabajo están en los registros de Google Cloud bajo el id de esta ejecución — la salida fila por fila de una importación son datos propios del negocio, y esta consola nunca guarda ninguno.',
  'jobs.picker.choose': 'elija un negocio…',
  'jobs.picker.failed': ({ message }) => `No se pudieron listar los negocios: ${message}`,
  'jobs.state.running': 'en curso',
  'jobs.state.succeeded': 'correcta',
  'jobs.state.failed': 'fallida',
  'jobs.state.unknown': 'desconocido',

  /* ---------------------------------------------------------------- */
  /* El asistente de incorporación                                     */
  /* ---------------------------------------------------------------- */

  'wizard.title': 'Añadir un negocio',
  'wizard.openTenant': 'Abrir la página del negocio',
  'wizard.step.create': 'Crear el negocio',
  'wizard.step.modules': 'Activar funciones',
  'wizard.step.key': 'Emitir la clave del socio',
  'wizard.step.webhook': 'Enviar los avisos de reservas al socio',
  'wizard.step.catalog': 'Cargar lo que venden',
  'wizard.step.conformance': 'Ejecutar la comprobación del contrato con socios',

  'wizard.create.title': 'Crear el negocio',
  'wizard.create.note':
    'Usted elige el id, y nunca se reutiliza. El negocio empieza en Configuración: a los socios se les dice que su disponibilidad es aproximada hasta que usted lo pase a En marcha, que es el traspaso y no un paso de aquí.',
  'wizard.create.tenantId': 'Id del negocio',
  'wizard.create.tenantIdHint':
    '`t_`, luego letras minúsculas, dígitos y guiones bajos — `t_lovespa_frenchtown`.',
  'wizard.create.timezone': 'Zona horaria',
  'wizard.create.timezoneHint':
    'Nombre IANA completo. La disponibilidad se calcula en ella — una equivocada es una reserva con una hora de diferencia.',
  'wizard.create.currency': 'Moneda',
  'wizard.create.submit': 'Crear el negocio',

  'wizard.modules.title': 'Activar funciones',
  'wizard.modules.note':
    'Lo que necesita cada función se comprueba contra todo lo que se pide a la vez, así que todo el camino del socio va en un solo paso. Pida los socios de reservas sin la agenda y el servidor dice lo que falta — esa respuesta se muestra aquí en vez de adivinarse.',
  'wizard.modules.legend': 'Funciones',
  'wizard.modules.needs': ({ list }) => ` — necesita ${list}`,
  'wizard.modules.enabled': ({ list }) => `Habilitados: ${list}`,
  'wizard.modules.submit': 'Habilitar y continuar',

  'wizard.key.title': 'Emitir la clave del socio',
  'wizard.key.note':
    'Una clave de prueba con lo que necesita el conserje — no puede cambiar datos de clientes, y no llega a nada de productos, cobro, tarjetas de regalo, saldo a favor, pagos ni informes.',
  'wizard.key.scopes': ({ scopes }) =>
    `Puede: ${scopes}. Es una clave de prueba, y lo dice ella misma, así que nadie tiene que ` +
    `adivinar cuál está mirando.`,

  'wizard.webhook.title': 'Enviar los avisos de reservas al socio',
  'wizard.webhook.note':
    'A dónde van los avisos de reservas. Solo https; la referencia al secreto es el nombre del secreto en Google Cloud, nunca el secreto — se busca cada vez que se envía un aviso.',
  'wizard.webhook.created': ({ url }) => `Se creó → ${url}`,
  'wizard.webhook.updated': ({ url }) => `Se actualizó → ${url}`,
  'wizard.webhook.skip': 'Omitir por ahora',
  'wizard.webhook.submit': 'Guardar y continuar',

  'wizard.catalog.title': 'Cargar lo que venden',
  'wizard.catalog.note': 'Ahora hay tres sitios donde hacerlo. El asistente no es uno de ellos.',
  'wizard.catalog.heading':
    'Un negocio no se puede reservar hasta que tiene servicios, las personas y salas que los hacen, y horarios',
  'wizard.catalog.body':
    'Este paso decía que la consola no podía hacerlo y no iba a aprender. Ha aprendido: la pestaña Lo que venden de la página del negocio carga un archivo de catálogo y edita lo que sale, porque una clienta real con una sesión de fotos en la agenda no debería necesitar a alguien en un terminal. Lo que no ha cambiado es el límite que hay debajo — los precios y las duraciones solo se pueden cambiar mientras el negocio se está configurando, y su libro, sus clientes y su dinero siguen tan fuera de alcance como siempre.',
  'wizard.catalog.rule': ({ tenantId }) =>
    `Este paso sigue preguntando en vez de comprobar, y ahora eso es una decisión sobre ` +
    `**dónde** y no sobre permisos: cargar el catálogo de \`${tenantId}\` es un archivo que alguien lee y ` +
    `una simulación que alguien mira, y eso va en la página del negocio, junto al catálogo ` +
    `que produce — no detrás de un botón de Siguiente.`,
  'wizard.catalog.two': 'Tres sitios pueden hacerlo, y todos quedan auditados:',
  'wizard.catalog.owner.before':
    '**El dueño, en su propia app del personal** — Configuración → servicios, recursos y horarios, con su propia sesión en ',
  'wizard.catalog.owner.after':
    '. Este es el camino que escala, porque es la persona que conoce el menú.',
  'wizard.catalog.cli': ({ tenantId }) =>
    `**La línea de comandos** — \`pnpm --filter @yourstore/cli cli -- catalog seed --tenant ${tenantId} …\`, ` +
    `para un traslado desde otro sistema, un negocio de demostración, o cualquier cosa que un archivo describa ` +
    `mejor que un formulario.`,
  'wizard.catalog.console':
    '**Lo que venden, en la página del negocio** — pegue su archivo, haga la simulación, cárguelo y luego vista lo que salga: descripciones, encabezados, orden y sus propias fotografías.',
  'wizard.catalog.confirm': ({ tenantId }) =>
    `El catálogo de \`${tenantId}\` ya está cargado. Lo verifiqué en la página del negocio, ` +
    `en la app del personal o en la línea de comandos.`,

  'wizard.conformance.title': 'Ejecutar la comprobación del contrato con socios',
  'wizard.conformance.note':
    'Comprueba cada promesa del acuerdo con los socios contra este negocio. Verde aquí significa que un socio puede confiar en él.',
  'wizard.conformance.notLive': ({ tenantId, status }) =>
    `${tenantId} está en ${status}, así que a los socios se les dice que su disponibilidad es ` +
    `aproximada, y las comprobaciones que necesitan un negocio en marcha no van a pasar. Pasarlo a ` +
    `En marcha es el traspaso — se hace desde la página del negocio, deliberadamente no como un clic ` +
    `al final de este camino.`,
  'wizard.conformance.failedTasks': ({ count }) =>
    ` — ${String(count)} ${plural(count, 'fallida', 'fallidas')}`,
  'wizard.conformance.green': ({ tenantId }) =>
    `Verde. ${tenantId} responde cada obligación que hace el acuerdo — puede tomar reservas de socios.`,
  'wizard.conformance.red':
    'Rojo. Las verificaciones que fallaron están en los registros de esta ejecución en Cloud Logging; corrija y vuelva a ejecutar.',
  'wizard.conformance.again': 'Ejecutar de nuevo',
  'wizard.conformance.finish': 'Terminar — abrir la página del negocio',

  /* ---------------------------------------------------------------- */
  /* Marca — el editor de tema (spec §6.4.3)                           */
  /* ---------------------------------------------------------------- */

  'branding.publish': 'Publicar',
  'branding.published': ({ version, bytes, effective }) =>
    `Se publicó la versión ${String(version)} (${String(bytes)} bytes). ${effective}`,
  'branding.publishNotePlaceholder': 'por qué este cambio',

  'branding.historyEmpty': ({ kept }) =>
    `Todavía no se conserva ninguna versión anterior. Se guardan las últimas ${String(kept)}; ` +
    `cualquier cosa más vieja se recupera de una copia de seguridad, no con un botón.`,

  'branding.useTemplate': 'Usar esta plantilla',
  'branding.templateTitle': ({ label }) => `¿Empezar desde ${label}?`,
  'branding.templateChosen': ({ label }) => `Empezado desde ${label}.`,
  'branding.shopping': 'Fotografías que traer',
  'branding.shoppingHint':
    'Cada marcador que muestra la plantilla, con la forma que necesita. Pegue la dirección https: de cada foto en el bloque que nombra.',
  'branding.shoppingItem': ({ count, ratio, where }) =>
    count === 0 ? `${where} — ${ratio}` : `${String(count)} × ${ratio} para ${where}`,

  'branding.siteTitle': 'Título del sitio',
  'branding.tagline': 'Lema',

  'branding.tokensChanged': ({ count }) =>
    `${String(count)} ${plural(count, 'cambiado', 'cambiados')}`,
  'branding.colourPicker': ({ label }) => `selector de color de ${label}`,

  'branding.motion': 'Movimiento',

  /* ---------------------------------------------------------------- */
  /* La página de contenido — la cuarta forma de página                */
  /* ---------------------------------------------------------------- */

  'branding.pages': 'Páginas',
  'branding.pagesHint': ({ max }) =>
    `Una página independiente de palabras en su propia dirección — la historia, las preguntas, ` +
    `las políticas, las ocasiones. Hasta ${String(max)}, cada una compuesta con los mismos ` +
    `bloques que la página de inicio, y cada una alcanzable desde la barra de navegación de ` +
    `abajo como \`page:<dirección>\`.`,
  'branding.pagesNone':
    'Todavía no hay ninguna página de contenido. Las preguntas, la historia y las ocasiones van aquí y no como bandas de la página de inicio, que es justo lo que la lista de bloques de arriba ya no ofrece.',
  'branding.pageAddress': ({ slug }) => `/p/${slug}`,
  'branding.pageTitle': 'Título',
  'branding.pageTitleHint':
    'El encabezado, la pestaña del navegador y la palabra que muestra la navegación.',
  'branding.pageSlug': 'Dirección',
  'branding.pageSlugPlaceholder': 'preguntas',
  'branding.pageTitlePlaceholder': 'Preguntas',
  'branding.addPage': 'Agregar página',
  'branding.pageLinked': 'en la navegación',
  'branding.pageUnlinked': 'fuera de la navegación',
  'branding.removePage': ({ slug }) => `Quitar /p/${slug}`,
  'branding.removePageTitle': ({ slug }) => `¿Quitar la página /p/${slug}?`,
  'branding.removePageConsequence': ({ slug }) =>
    `La página y todo lo compuesto en ella salen de este borrador. Nada se publica hasta que ` +
    `publique, y la versión que todavía tiene /p/${slug} queda en el historial de todos modos.`,
  'branding.removePageLinked': ({ label }) =>
    `La entrada de navegación “${label}” apunta a ella: quite también ese enlace, o apúntelo a ` +
    `otro lado, o se rechaza la publicación.`,

  'branding.slugTaken': ({ slug }) => `Este documento ya tiene una página en /p/${slug}.`,
  'branding.slugLong': ({ max }) =>
    `Una dirección tiene a lo sumo ${String(max)} caracteres — es algo que una persona lee en voz alta.`,
  'branding.slugShape':
    'Una dirección son minúsculas, dígitos y guiones simples entre ellos: `qué-traer` se escribe `que-traer`, no `Qué Traer`.',
  'branding.slugReserved': ({ slug }) =>
    `\`${slug}\` es una dirección que ya pertenece a una ruta, así que una página escrita ahí se ` +
    `guardaría y no se le mostraría jamás a ningún huésped.`,
  'branding.pagesFull': ({ max }) =>
    `${String(max)} páginas de contenido es el techo. Todas viajan en la única fila de marca que ` +
    `guest-api lee completa en cada carga de página.`,

  'branding.inLanguage': ({ label, language }) => `${label} (${language})`,
  'branding.addLanguage': ({ label, language }) => `Escribir ${label.toLowerCase()} en ${language}`,
  'branding.dropLanguage': ({ label, language }) => `Quitar ${label.toLowerCase()} en ${language}`,
  'branding.plusLanguage': ({ language }) => `+ ${language}`,

  /* ---------------------------------------------------------------- */
  /* La barra de navegación                                            */
  /* ---------------------------------------------------------------- */

  'branding.navHint':
    'La barra que lleva cada página. Es interna por gramática — no hay forma en la que quepa una URL — y un enlace a una página de contenido es `page:<dirección>`. Un destino de otra forma (un ancla de la página de inicio, una categoría, una vista interna) todavía no tiene editor aquí y se conserva exactamente como está guardado.',
  'branding.navNone': 'No hay barra de navegación configurada. El sitio muestra su armazón mínimo.',
  'branding.navLabel': ({ n }) => `etiqueta del enlace ${String(n)}`,
  'branding.navTargetLabel': 'Va a',
  'branding.navTarget': ({ n }) => `destino del enlace ${String(n)}`,
  'branding.navRemove': ({ n }) => `Quitar el enlace ${String(n)}`,
  'branding.navLink': ({ title }) => `Enlazar ${title} desde la navegación`,
  'branding.navUndeclared': ({ target }) =>
    `\`${target}\` — la navegación apunta a una página que este documento no declara.`,
  'branding.navUndeclaredHint':
    'Vuelva a agregar la página, o apunte el enlace a otro lado. La publicación se rechaza igual — este es el mismo rechazo del servidor, hecho antes de la solicitud en vez de después.',

  'branding.survivedUnchanged': 'Publicado tal como se escribió — el saneador no cambió nada.',
  'branding.survived': ({ removed, before, after }) =>
    `Saneado al publicar: se quitó ${removed} (${String(before)} → ${String(after)} bytes). Lo ` +
    `que se guarda, y lo que recibe cada huésped, es:`,

  'branding.rowLabel': ({ label, n }) => `etiqueta ${String(n)} de ${label}`,
  'branding.rowValue': ({ label, n }) => `valor ${String(n)} de ${label}`,
  'branding.addRow': 'Agregar fila',
  'branding.rowKind': ({ label, n }) => `tipo ${String(n)} de ${label}`,
  'branding.policyKind.none': '— ninguno',
  'branding.policyKind.cancellation': 'Cancelación',
  'branding.policyKind.lead_time': 'Reservar con antelación',
  'branding.policyKind.request_hold': 'Solicitudes',
  'branding.policyKind.pickup_expiry': 'Recogida',
  'branding.policyKind.deposit': 'Depósito',
  'branding.policyKind.other': 'Otro',
  'branding.policyOverridden': ({ rule }) =>
    `Sin efecto: se muestra la regla del motor en su lugar: ${rule}`,
  'branding.policySuspected': ({ rules }) =>
    `Parece una regla que el motor aplica; compárela con la del motor: ${rules}`,
  'branding.policyRule.lead_time': ({ minutes }) =>
    `se reserva con al menos ${String(minutes)} min de antelación`,
  'branding.policyRule.same_day_cutoff': ({ time }) =>
    `las reservas para el mismo día cierran a las ${time}`,
  'branding.policyRule.request_hold': ({ hours }) =>
    `una solicitud guarda su hora durante ${String(hours)} h`,
  'branding.policyRule.pickup_expiry': ({ days }) =>
    `un pedido listo espera en el estante ${String(days)} ${plural(days, 'día', 'días')}`,
  'branding.policyRule.deposit_percent': ({ percent, minCents }) =>
    `un depósito del ${String(percent)} %, mínimo ${(minCents / 100).toFixed(2)}`,
  'branding.policyRule.deposit_fixed': ({ cents }) =>
    `un depósito fijo de ${(cents / 100).toFixed(2)}`,
  'branding.policyRule.house_gap': ({ minutes }) => `${String(minutes)} min entre citas`,
  'branding.policyRule.waiver': ({ title }) => `se firma una exención: ${title}`,
  'branding.stringLabel': ({ label, n }) => `${label} ${String(n)}`,

  'branding.css': 'Hoja de estilos propia',
  'branding.cssHint':
    'El único estilo que escribe el propio negocio. Selectores de clase y de elemento, `@media` si lo necesita — sin `@import`, sin `url()`, sin ids. El servidor rechaza el resto en vez de editarlo: una hoja de estilos la escribe alguien que quiso decir cada carácter.',
  'branding.cssBytes': ({ bytes, limit }) => `${String(bytes)} / ${String(limit)} bytes`,
  'branding.cssPinned': ({ version }) =>
    ` · la hoja publicada se escribió para la versión ${String(version)} del tema del sitio; ` +
    `revísela de nuevo cuando el tema cambie.`,
  'branding.cssNotYet':
    'El sitio web todavía no la usa — se guarda y se comprueba ahora, lista para cuando la use.',

  'branding.sanitizer': 'Qué puede contener una zona de contenido',
  'branding.sanitized': ({ count }) =>
    `${String(count)} ${plural(count, 'saneada', 'saneadas')} en la última publicación`,
  'branding.allowed': 'Permitido:',
  'branding.linkSchemes': ({ schemes }) => `Los enlaces solo pueden ser ${schemes.join(' o ')}.`,

  /* ---------------------------------------------------------------- */
  /* El conserje (la integración con aiconcierge)                      */
  /*                                                                   */
  /* Los *tokens* de aiconcierge — `balanced`, `ai_first`,             */
  /* `provider_connection` — no se traducen, por la misma razón que no */
  /* se traducen los módulos: quien lea `balanced` aquí y otra palabra */
  /* en la otra consola tendría dos nombres para un mismo ajuste. Lo   */
  /* que sí se traduce es la frase que va **al lado** de cada token.   */
  /* ---------------------------------------------------------------- */

  'concierge.note':
    'La IA que contesta el teléfono y el chat. Lo que el agente **sabe** — horarios, precios, políticas, preguntas — es del negocio y se edita donde ya vive; lo que el agente **hace** se define aquí.',
  'concierge.notConfigured': 'La integración del conserje no está configurada en este despliegue.',
  'concierge.notConfiguredVars': ({ vars }) =>
    `Falta: ${vars}. Este negocio no tiene ningún problema — sencillamente este despliegue no tiene con quién hablar.`,

  'concierge.enable': 'Activar el conserje',
  'concierge.enabling': 'Activando…',
  'concierge.enabled': ({ prefix }) =>
    `Activado. Una clave (${prefix}…) y un secreto de firma viajaron de máquina a máquina — ninguno pasó por este navegador.`,

  'concierge.wizard.title': 'El conserje',
  'concierge.wizard.note':
    'Una sola acción: las funciones que necesita activadas, una clave de acceso creada, un secreto de firma guardado, el negocio añadido del lado del conserje y los avisos de reservas apuntados de vuelta aquí. La clave y el secreto nunca pasan por este navegador — esa es toda la razón por la que este paso reemplazó al copiar y pegar.',
  'concierge.wizard.other':
    'Cualquier otro socio conserva el camino anterior: una clave que se muestra una sola vez y luego la dirección para los avisos de reservas escrita a mano.',

  /* ---------------------------------------------------------------- */
  /* El cuestionario del propietario (docs/tenant-intake-design.md)    */
  /* ---------------------------------------------------------------- */

  'loading.intake': 'las respuestas del propietario',
  'loading.photos': 'las fotografías',

  'intake.notSentNote':
    'Elige la forma de su página y el idioma en que lee, y el enlace aparece en la pantalla siguiente. Es la única copia — envíalo, no lo guardes.',

  'intake.template': 'Plantilla',
  'intake.templateHint':
    'La forma de su página. Decide qué preguntas se le hacen, y puedes cambiar de idea más tarde.',
  'intake.language': 'Idioma',
  'intake.languageHint': 'El idioma en que se abre el formulario. Puede contestar en otro.',

  'intake.mint': 'Crear el enlace',
  'intake.remint': 'Crear un enlace nuevo',
  'intake.remintConsequence':
    'El enlace que ya tiene deja de funcionar en cuanto se cree este — si lo tiene abierto en el móvil, dejará de guardar. Sus respuestas se conservan. Envíale el nuevo.',
  'intake.linkLabel': ({ host }: { host: string }) => `El enlace del propietario · ${host}`,
  'intake.messageLabel': 'El mensaje que hay que enviar',
  'intake.copyMessage': 'Copiar el mensaje',
  'intake.message.en': ({ link }: { link: string }) =>
    `Hi — this is the form for your new page. It takes about 20 minutes, saves as you go, and you can add photos from your phone: ${link}`,
  'intake.message.es': ({ link }: { link: string }) =>
    `Hola — este es el formulario para tu nueva página. Toma unos 20 minutos, se guarda solo mientras escribes y puedes añadir fotos desde el móvil: ${link}`,

  'intake.start.warnings': 'Conviene mirarlo antes de empezar',
  'intake.start.unplaced':
    'Estas respuestas no están en la página. Pégalas donde las quieras, o acórtalas primero.',
  'intake.start.done': ({ template }: { template: string }) =>
    `El editor de abajo es ahora un borrador de ${template} con las palabras del propietario. Todavía no se ha publicado nada.`,

  /* ---- el catálogo (GS4, migración 0050) ------------------------- */

  'intake.menu.seed': 'Sembrar esto',
  'catalog.notGranted':
    'Este despliegue todavía no se ha actualizado para que la consola cambie lo que vende un negocio. Hasta entonces, el catálogo se carga desde la línea de comandos del operador, y las fotografías también.',
  'catalog.tab.scheduled': 'Servicios',
  'catalog.tab.stocked': 'Tienda',
  'catalog.tab.prepared': 'Menú',
  'catalog.tab.stay': 'Habitaciones',
  'catalog.tab.departure': 'Salidas',
  'catalog.tab.other': 'Todo lo demás',
  'catalog.vertical.pool': ({ id }: { id: string }) => `Grupo ${id}`,
  'catalog.vertical.vessel': ({ id }: { id: string }) => `Embarcación ${id}`,
  'catalog.vertical.sleeps': ({ n }: { n: number }) => `Para ${String(n)}`,
  'catalog.vertical.nightly': ({ price }: { price: string }) => `Desde ${price} la noche`,
  'catalog.vertical.calendar': ({ bands, exceptions }: { bands: number; exceptions: number }) =>
    `${String(bands)} temporada(s), ${String(exceptions)} noche(s) con precio propio`,
  'catalog.vertical.minNights': ({ n }: { n: number }) => `Mínimo ${String(n)} noche(s)`,
  'catalog.vertical.checkTimes': ({ inAt, outAt }: { inAt: string; outAt: string }) =>
    `Entrada ${inAt}, salida ${outAt}`,
  'catalog.vertical.seats': ({ n }: { n: number }) => `${String(n)} plazas`,
  'catalog.vertical.charter': ({ n }: { n: number }) => `Chárter privado, ${String(n)} plazas`,
  'catalog.vertical.length': ({ min }: { min: number }) => `${String(min)} min`,
  'catalog.vertical.starts': ({ n }: { n: number }) => `${String(n)} salida(s) por semana`,
  'catalog.vertical.season': ({ from, to }: { from: string; to: string }) =>
    `Temporada del ${from} al ${to}`,
  'catalog.vertical.yearRound': 'Todo el año',
  'catalog.vertical.tiers': 'Tipos de billete',
  'catalog.vertical.soldOut': 'Agotado',
  'catalog.vertical.offPartner': 'Fuera del tablero del agente de IA',
  'catalog.vertical.offSite': 'Fuera del sitio web',
  'catalog.name': 'Nombre',
  'catalog.nameOf': ({ item }: { item: string }) => `Nombre de ${item}`,
  'catalog.price': 'Precio',
  'catalog.priceOf': ({ item }: { item: string }) => `Precio de ${item}`,
  'catalog.duration': 'Minutos',
  'catalog.durationOf': ({ item }: { item: string }) => `Duración de ${item}`,
  'catalog.bufferBefore': 'Margen antes',
  'catalog.bufferBeforeOf': ({ item }: { item: string }) => `Margen antes de ${item}`,
  'catalog.bufferAfter': 'Margen después',
  'catalog.bufferAfterOf': ({ item }: { item: string }) => `Margen después de ${item}`,
  'catalog.houseDefault': ({ minutes }: { minutes: number | null }) =>
    minutes === null ? 'valor de la casa' : `valor de la casa (${String(minutes)})`,
  'catalog.bufferHint': ({ minutes }: { minutes: number | null }) =>
    minutes === null
      ? 'Minutos. En blanco toma el valor de la casa; 0 es ninguno.'
      : `Minutos. En blanco toma el valor de la casa (${String(minutes)}); 0 es ninguno.`,
  'catalog.category': 'Encabezado',
  'catalog.categoryOf': ({ item }: { item: string }) => `Encabezado de ${item}`,
  'catalog.sortOrder': 'Posición',
  'catalog.sortOrderHint': 'El menor va primero. Los empates se resuelven por el id.',
  'catalog.sortOrderOf': ({ item }: { item: string }) => `Posición de ${item}`,
  'catalog.brand': 'Marca',
  'catalog.brandHint': 'Quién lo fabrica — se imprime en la ficha del producto.',
  'catalog.brandOf': ({ item }: { item: string }) => `Marca de ${item}`,
  'catalog.descriptionHint': 'La frase que lee un huésped antes de reservarlo.',
  'catalog.descriptionOf': ({ item }: { item: string }) => `Descripción de ${item}`,
  /* Opciones y complementos (migración 0048, W5c). */
  'catalog.options': 'Formas en que se vende',
  'catalog.options.headline':
    'Siguen a la duración marcada abajo — el servidor las mantiene sincronizadas.',
  'catalog.options.name': 'Se llama',
  'catalog.options.nameOf': ({ item, ref }: { item: string; ref: string }) =>
    `Cómo se llama la duración ${ref} de ${item}`,
  'catalog.options.nameIn': ({ language }) => `También en ${language}`,
  'catalog.options.nameInOf': ({ item, ref, language }) =>
    `Cómo se llama la duración ${ref} de ${item} en ${language}`,
  'catalog.options.newNameIn': ({ item, language }) =>
    `Cómo se llama una nueva duración de ${item} en ${language}`,
  'catalog.options.language.en': 'inglés',
  'catalog.options.language.es': 'español',
  'catalog.options.price': 'Precio',
  'catalog.options.priceOf': ({ item, ref }: { item: string; ref: string }) =>
    `Precio de la duración ${ref} de ${item}`,
  'catalog.options.minutes': 'Minutos',
  'catalog.options.minutesHint': 'Déjelo en blanco si esta no cambia cuánto se tarda.',
  'catalog.options.minutesOf': ({ item, ref }: { item: string; ref: string }) =>
    `Minutos de la duración ${ref} de ${item}`,
  'catalog.options.default': 'La que se cita',
  'catalog.options.defaultOf': ({ item, ref }: { item: string; ref: string }) =>
    `Citar la duración ${ref} de ${item} por defecto`,
  'catalog.options.active': 'Todavía se vende',
  'catalog.options.activeOf': ({ item, ref }: { item: string; ref: string }) =>
    `Si la duración ${ref} de ${item} todavía se vende`,
  'catalog.options.up': ({ item, ref }: { item: string; ref: string }) =>
    `Adelantar la duración ${ref} de ${item}`,
  'catalog.options.down': ({ item, ref }: { item: string; ref: string }) =>
    `Retrasar la duración ${ref} de ${item}`,
  'catalog.options.save': 'Guardar esta duración',
  'catalog.options.saveOf': ({ item, ref }: { item: string; ref: string }) =>
    `Guardar la duración ${ref} de ${item}`,
  'catalog.options.add': 'Añadir una duración',
  'catalog.options.addNote':
    'Los precios son lo que cuesta el conjunto, nunca un extra encima — una propietaria escribe 85, 170, 255, y no 85 y dos «más 85». No hay borrado: una duración que alguien compró todavía tiene que aparecer en el informe de marzo, así que retírela en su lugar.',
  'catalog.options.ref': 'Clave corta',
  'catalog.options.refTaken': ({ ref }: { ref: string }) =>
    `Este ya tiene una duración con la clave ${ref} — elija otra clave, o edite esa arriba.`,
  'catalog.options.newRef': ({ item }: { item: string }) =>
    `Clave corta de una nueva duración de ${item}`,
  'catalog.options.newName': ({ item }: { item: string }) =>
    `Cómo se llama una nueva duración de ${item}`,
  'catalog.options.newPrice': ({ item }: { item: string }) =>
    `Precio de una nueva duración de ${item}`,
  'catalog.options.newMinutes': ({ item }: { item: string }) =>
    `Minutos de una nueva duración de ${item}`,
  'catalog.options.create': 'Añadir esta duración',
  'catalog.options.createOf': ({ item }: { item: string }) => `Añadir una duración a ${item}`,
  'catalog.addOns': 'Qué se le puede añadir',
  'catalog.addOns.empty': 'Todavía no se le puede añadir nada a este.',
  'catalog.addOns.paused': 'en pausa',
  'catalog.addOns.unlink': 'Desvincular',
  'catalog.addOns.unlinkOf': ({ item, name }: { item: string; name: string }) =>
    `Dejar de ofrecer ${name} con ${item}`,
  'catalog.addOns.pickHint':
    'Sigue siendo un servicio propio; esto solo dice que puede ir acompañando.',
  'catalog.addOns.price': 'Precio añadido',
  'catalog.addOns.priceOf': ({ item }: { item: string }) => `Precio añadido a ${item}`,
  'catalog.addOns.priceHint': 'En blanco significa lo mismo que cuesta por su cuenta.',
  'catalog.addOns.minutes': 'Minutos añadidos',
  'catalog.addOns.minutesOf': ({ item }: { item: string }) => `Minutos añadidos a ${item}`,
  'catalog.addOns.minutesHint':
    'En blanco significa su propia duración. El 0 es real — no añade tiempo.',
  'catalog.addOns.link': 'Vincularlo',
  'catalog.addOns.linkOf': ({ item }: { item: string }) => `Vincular un complemento a ${item}`,
  'catalog.save': 'Guardar',
  'catalog.saved': 'Guardado.',
  'catalog.photosTitle': 'Fotografías',
  'catalog.photosEmpty': 'Todavía no hay fotografías en esta.',
  'catalog.alt': 'Qué muestra',
  'catalog.altOf': ({ position }: { position: number }) =>
    `Qué muestra la fotografía ${String(position)}`,
  'catalog.altRequired':
    'Cada fotografía necesita una línea que diga qué muestra antes de guardar.',
  'catalog.moveUp': ({ position }: { position: number }) =>
    `Adelantar la fotografía ${String(position)}`,
  'catalog.moveDown': ({ position }: { position: number }) =>
    `Retrasar la fotografía ${String(position)}`,
  'catalog.removePhoto': ({ position }: { position: number }) =>
    `Quitar la fotografía ${String(position)}`,
  'catalog.remove': 'Quitar',
  'catalog.forThisItem': 'Tomadas de esta',
  'catalog.pick': 'Elegir…',
  'catalog.pasteUrl': 'O pegue la dirección de una imagen',
  'catalog.addPhoto': 'Añadir',
  'catalog.savePhotos': 'Guardar fotografías',
  'catalog.photosSaved': 'Fotografías guardadas, en este orden.',
  'catalog.categories': 'Encabezados',
  'catalog.categoriesNote':
    'Un encabezado es una etiqueta en cada cosa y no una página propia, así que renombrarlo cambia todas las cosas archivadas bajo él — que es la única forma de hacerlo sin acabar con dos páginas que se diferencian en una coma.',
  'catalog.renameFrom': 'Renombrar',
  'catalog.renameTo': 'A',
  'catalog.renameToHint': 'Déjelo en blanco para sacarlas de cualquier encabezado.',
  'catalog.rename': 'Renombrar en todas partes',
  'catalog.renamed': ({ items, to }: { items: number; to: string }) =>
    `${String(items)} cosa(s) ahora archivadas bajo «${to}».`,
  'catalog.add': 'Añadir una cosa',
  'catalog.addNote':
    'Un servicio o un producto de estantería. Un plato, un tipo de habitación o una salida necesitan un archivo de catálogo en vez de un formulario — pegue uno abajo.',
  'catalog.kind': 'De qué tipo',
  'catalog.newName': 'Nombre de la cosa nueva',
  'catalog.newPrice': 'Precio de la cosa nueva',
  'catalog.newDuration': 'Minutos de la cosa nueva',
  'catalog.create': 'Añadirla',
  'catalog.seed': 'Sembrar desde un archivo de catálogo',
  'catalog.seedNote':
    'El mismo documento que lee la CLI del operador, con el mismo lector. Primero la prueba en seco — ejecuta lo real y lo deshace, así que lo que le dice es lo que pasaría.',
  'catalog.seedFile': 'El archivo',
  'catalog.dryRun': 'Prueba en seco',
  'catalog.seedThis': 'Sembrar esto',
  'catalog.seedDryRun': ({
    services,
    resources,
    stays,
    departures,
    version,
  }: {
    services: number;
    resources: number;
    stays: number;
    departures: number;
    version: number;
  }) =>
    `Sembraría ${String(services)} cosa(s), ${String(resources)} sala(s), ${String(stays)} tipo(s) de habitación y ${String(departures)} salida(s), dejando el catálogo en la versión ${String(version)}. No se escribió nada.`,
  'catalog.seedApplied': ({
    services,
    resources,
    stays,
    departures,
    version,
  }: {
    services: number;
    resources: number;
    stays: number;
    departures: number;
    version: number;
  }) =>
    `Sembradas ${String(services)} cosa(s), ${String(resources)} sala(s), ${String(stays)} tipo(s) de habitación y ${String(departures)} salida(s). El catálogo está ahora en la versión ${String(version)}.`,

  'wizard.step.intake': 'Pregúntale al propietario',
  'wizard.intake.title': 'Pídele al propietario que rellene la página',
  'wizard.intake.note':
    'Un enlace, veinte minutos en su móvil, y las palabras y las fotografías de su página vuelven escritas por quien conoce el negocio. Opcional — puedes rellenar la página a mano como antes.',
  'wizard.intake.skip': 'Saltar — escribiré la página yo',
  'wizard.intake.continue': 'Continuar',

  /* ---------------------------------------------------------------- */
  /* La página del negocio (rediseño de la consola, fase A)            */
  /* ---------------------------------------------------------------- */

  // Pronombres neutros: la consola no sabe cómo se nombra a cada propietario,
  // así que dice «el negocio», «el propietario» o el nombre del negocio.
  'business.tabs.label': 'Negocio',
  'business.tab.overview': 'Resumen',
  'business.tab.setup': 'Configuración',
  'business.tab.site': 'Sitio web',
  'business.tab.catalog': 'Lo que venden',
  'business.tab.connections': 'Conexiones',
  'business.tab.activity': 'Actividad',
  'business.actions.label': 'Acciones del negocio',
  'business.protected': 'Protegido — no se puede borrar',
  'business.protected.title':
    'Este negocio guarda datos reales. Nada puede borrarlo hasta que se quite la protección desde la línea de comandos, que deja su propio registro.',
  'business.details.title': 'Datos del negocio',

  'stage.track.label': 'Camino a estar en marcha',
  'stage.track.step': ({ n }) => `Etapa ${String(n)}`,
  'stage.track.later': 'Después',
  'stage.track.now': 'ahora',
  'stage.name.onboarding': 'En configuración',
  'stage.name.importing': 'Trayendo su agenda',
  'stage.name.live': 'En marcha',
  'stage.name.paused': 'En pausa',
  'stage.name.offboarded': 'Dado de baja',
  'stage.name.online': 'Reservas en línea',
  'stage.hint.onboarding': 'servicios, horario, su acceso',
  'stage.hint.importing': 'clientes y citas',
  'stage.hint.live': 'llevan su día aquí',
  'stage.hint.online': 'sitio web, mensajes, conserje',

  'stage.menu': 'Cambiar de etapa',
  'stage.option.onboarding': 'Volver a la configuración',
  'stage.option.importing': 'Empezar a traer su agenda',
  'stage.option.live': 'Poner en marcha',
  'stage.option.paused': 'Pausar',
  'stage.option.offboarded': 'Dar de baja…',
  'stage.meaning.onboarding':
    'A los socios se les dice que los horarios son aproximados mientras se termina la configuración.',
  'stage.meaning.importing':
    'Las reservas llegan desde su sistema anterior mientras lo siguen usando. A los socios se les dice que los horarios son aproximados.',
  'stage.meaning.live':
    'Su agenda aquí es la de verdad. A los socios se les dice que los horarios son exactos.',
  'stage.meaning.paused': 'Nadie puede reservar. No se pierde nada.',
  'stage.meaning.offboarded':
    'Terminó con nosotros. No se puede reservar nada; el id no se reutiliza nunca.',

  // Igual de graves que en inglés, a propósito: poner en marcha sigue siendo el traspaso.
  'stage.confirm.title.onboarding': ({ name }) => `¿Devolver ${name} a la configuración?`,
  'stage.confirm.title.importing': ({ name }) => `¿Empezar a traer la agenda de ${name}?`,
  'stage.confirm.title.live': ({ name }) => `¿Poner ${name} en marcha?`,
  'stage.confirm.title.paused': ({ name }) => `¿Pausar ${name}?`,
  'stage.confirm.title.offboarded': ({ name }) => `¿Dar de baja ${name}?`,
  'stage.consequence.onboarding': ({ name }) =>
    `A los socios se les deja de decir que los horarios de ${name} son exactos. Un asistente que esté hablando ahora con un cliente empezará a decir que sus horarios son aproximados.`,
  'stage.consequence.importing': ({ name }) =>
    `Se pueden traer los clientes y las citas de ${name} mientras siguen trabajando en su sistema anterior. A los socios se les dice que los horarios son aproximados hasta que se pongan en marcha, porque el sistema anterior todavía puede tomar una reserva.`,
  'stage.consequence.live': ({ name }) =>
    `Este es el traspaso. Desde ahora la agenda de ${name} aquí es la de verdad: a los socios se les dice que los horarios son exactos y se los prometerán a los clientes. Hazlo solo después de la hora del traspaso con el propietario.`,
  'stage.consequence.paused': ({ name }) =>
    `Nadie puede reservar con ${name} — ni en la aplicación, ni en línea, ni a través de un socio. Los socios lo rodean en vez de fallar en plena conversación. No se pierde nada y puedes reanudarlo cuando quieras.`,
  'stage.consequence.offboarded': ({ name }) =>
    `${name} terminó con nosotros. No se puede reservar nada, a los socios se les dice que no hay nada que reservar y el id no se vuelve a usar nunca. Sus datos no se borran.`,
  'stage.confirm.onboarding': 'Devolver',
  'stage.confirm.importing': 'Empezar',
  'stage.confirm.live': 'Poner en marcha',
  'stage.confirm.paused': 'Pausar',
  'stage.confirm.offboarded': 'Dar de baja',
  'stage.moved': ({ name, stage, seconds }) =>
    `${name} ya está en «${stage}». Los socios ven el cambio en ${String(seconds)} segundos.`,
  'stage.toast': ({ name, stage }) => `${name}: ${stage}`,

  'jobs.executions.scopeLabel': 'De quién son las ejecuciones',
  'jobs.executions.scope.tenant': ({ tenantId }) => `Solo ${tenantId}`,
  'jobs.executions.scope.all': 'Todos los negocios',
  'jobs.executions.scopeNote':
    'Cloud Run no registra para qué negocio fue cada ejecución, así que se cotejan con las que el propio registro de este negocio dice que se iniciaron para él.',
  'jobs.executions.scopeEmpty': 'Ninguna ejecución de esta lista se inició para este negocio.',
};
